import { PropsWithChildren, useCallback, useMemo } from 'react';

import { useUser as useAuth0User } from '@auth0/nextjs-auth0/client';

import { AuthenticationContext } from '../contexts/authentication-context';
import { useTokenContext } from '../hooks/use-token-context';
import { User } from '../models/user.model';

export const AuthenticationContextProvider = ({ children }: PropsWithChildren) => {
   const { user: auth0User, isLoading: isLoadingUser, checkSession } = useAuth0User();
   const { accessToken, isLoading: isLoadingToken, refreshToken: refreshTokenFn } = useTokenContext();

   const refreshToken = useCallback(async () => {
      await refreshTokenFn();
      await checkSession();
   }, []);

   const login = useCallback((redirectUrl?: string) => {
      const locale = window.location.pathname.split('/')[1];
      const language = locale.split('-')[0].toLowerCase();
      const searchParams = new URLSearchParams({ language });

      if (redirectUrl) {
         searchParams.set('returnTo', redirectUrl);
      }

      const url = new URL(`/api/auth/login?${searchParams.toString()}`, window.location.origin);
      window.location.href = url.toString();
   }, []);

   const logout = useCallback(() => {
     const url = new URL(`/api/auth/logout`, window.location.origin);
     window.location.href = url.toString();
   }, []);

   const value = useMemo(
      () => ({
         user: auth0User as User | undefined,
         accessToken,
         loading: isLoadingUser || isLoadingToken,
         refreshToken,
         login,
         logout
      }),
      [auth0User, accessToken, isLoadingUser, isLoadingToken, refreshToken, login, logout]
   );

   return <AuthenticationContext.Provider value={value}>{children}</AuthenticationContext.Provider>;
};
